import './default-theme.processed.css';
import './appearance-fonts.css';
import './appearance-themes.css';
import './appearance-bridge.css';
import './index.css';

import { App, consumePwaLaunchParams } from '@bangle.io/app';
import { ThemeManager } from '@bangle.io/color-scheme-manager';
import { APP_BUILD_ID, APP_BUILD_TIME } from '@bangle.io/config';
import { THEME_MANAGER_CONFIG } from '@bangle.io/constants';
import {
  createEditorSaveCoordinator,
  initializeServices,
} from '@bangle.io/initialize-services';
import { Logger } from '@bangle.io/logger';
import { createStore } from 'jotai';
import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { setupAppBuildGuard } from './app-build-guard';
import { setupRootEmitter } from './setup-root-emitter';

const isDebug =
  window.location.hostname === 'localhost' ||
  window.location.search.includes('debug=true');

const startupLogger = new Logger('', isDebug ? 'debug' : 'info');
const editorSaveCoordinator = createEditorSaveCoordinator();

void main(startupLogger).catch((error) => {
  handleStartupFailure(error, startupLogger);
});

async function main(logger: Logger) {
  // Consume PWA launch params (?launch=, ?shortcut=) before any service is
  // constructed: the router captures `window.location.search` at creation
  // and re-emits it on every navigation, so a param still present here
  // would be re-added to the URL and replayed on reload. Idempotent, so the
  // in-app fallback consumer stays harmless.
  consumePwaLaunchParams(window);

  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  const abortController = new AbortController();
  const tabId = `tab_${Math.random().toString(36).substr(2, 9)}`;

  const rootEmitter = setupRootEmitter(
    'sariz_notes_channel',
    tabId,
    logger,
    abortController.signal,
  );
  const appBuildGuard = setupAppBuildGuard({
    rootEmitter,
    appBuild: { id: APP_BUILD_ID, builtAt: APP_BUILD_TIME },
    tabId,
    logger: logger.child('app-build-guard'),
    abortSignal: abortController.signal,
  });

  const store = createStore();
  const themeManager = new ThemeManager(THEME_MANAGER_CONFIG);
  let services: Awaited<ReturnType<typeof initializeServices>>;
  try {
    services = await initializeServices(
      logger,
      rootEmitter,
      store,
      themeManager,
      abortController.signal,
      editorSaveCoordinator,
    );
  } catch (error) {
    abortController.abort();
    throw error;
  }

  const root = createRoot(rootElement);

  root.render(
    <StrictMode>
      <App
        logger={logger}
        services={services}
        store={store}
        rootEmitter={rootEmitter}
      />
    </StrictMode>,
  );

  // Services (including the stale-tab listener) must be mounted before a
  // running tab can reply to this announcement.
  appBuildGuard.start();

  if (isDebug) {
    (window as any).services = services;
  }

  abortController.signal.addEventListener('abort', () => {
    root.unmount();
  });

  rootEmitter.on(
    'event::app:reload-ui',
    () => {
      logger.info('-------------Reloading UI-------------');
      abortController.abort();
      queueMicrotask(() => {
        void main(logger).catch((error) => {
          handleStartupFailure(error, logger);
        });
      });
    },
    abortController.signal,
  );
}

function handleStartupFailure(error: unknown, logger: Logger) {
  logger.error('Unable to start Typo Notes', error);
  renderStartupError(error);
}

export function renderStartupError(error: unknown) {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    console.error('Unable to show startup failure because #root is missing');
    return;
  }

  createRoot(rootElement).render(
    <StrictMode>
      <StartupErrorView error={error} />
    </StrictMode>,
  );
}

function StartupErrorView({ error }: { error: unknown }) {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : t.app.pageStartupError.description;

  return (
    <main
      role="alert"
      aria-labelledby="startup-error-title"
      className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground"
    >
      <section className="w-full max-w-xl rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
        <h1 id="startup-error-title" className="font-semibold text-2xl">
          {t.app.pageStartupError.title}
        </h1>
        <p className="mt-3 text-muted-foreground">
          {t.app.pageStartupError.description}
        </p>
        <details className="mt-4 rounded-md border bg-muted/40 p-3">
          <summary className="cursor-pointer font-medium text-sm">
            {t.app.pageStartupError.detailsLabel}
          </summary>
          <pre className="mt-3 max-h-48 overflow-auto whitespace-pre-wrap break-words text-muted-foreground text-xs">
            {message}
          </pre>
        </details>
        <button
          className="mt-5 inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground text-sm"
          type="button"
          onClick={() => window.location.reload()}
        >
          {t.app.pageStartupError.reloadButton}
        </button>
      </section>
    </main>
  );
}
